import { createContext, useContext, useEffect, useState } from 'react';
import DateFilter from 'shared/modules/dateFilter.js';
import moment from 'moment';
import {
  marketPlaceFamilyTypes,
  marketPlaceService,
  PANELS_TYPES,
  supportServices,
} from 'app/containers/MainDashboard/consts.js';
import { useRootStore } from 'app/contexts/RootStoreContext.jsx';
import { CLOUD_TYPE_IDS } from 'users/constants/usersConstants.js';
import { AwsCommonFields } from 'shared/constants/awsConstants.js';
import { AzureCommonFields } from 'shared/constants/azureConstants.js';
import { getCostOptions } from 'usage/containers/CostAndUsageExplorer/helpers/customDashboardHelperMethods.js';
import { getExcludeOptions } from 'app/containers/MainDashboard/utils.js';
import { AMORTIZE_COST_TYPES, CostTypes } from 'usage/constants/costAndUsageConstants.js';

const initialPanelsState = {
  [PANELS_TYPES.MTD_COST]: {},
  [PANELS_TYPES.PREVIOUS_MONTH_COST]: {},
  [PANELS_TYPES.FORECASTED_MONTHLY_COST]: {},
  [PANELS_TYPES.OPEN_RECOMMENDATIONS]: {},
  [PANELS_TYPES.LINKED_ACCOUNT_TOTAL]: {},
  [PANELS_TYPES.ANNUAL_POTENTIAL_SAVINGS]: {},
  [PANELS_TYPES.MTD_TOP_10]: {},
  [PANELS_TYPES.HISTORY_DAILY_WITH_FORECAST]: {},
  [PANELS_TYPES.HISTORY_MONTHLY_WITH_FORECAST]: {},
  [PANELS_TYPES.REGION_COST]: {},
};

const UnSupportForecastFilter = 'Adjust the filters to view forecast data.';

const isCostSelectedCostTypeSupportForecast = (filters) => {
  if (filters.costType?.find((option) => [CostTypes.REFUND, CostTypes.CREDIT].includes(option.value))) {
    return false;
  }
  return !!filters.costType?.find(
    (option) =>
      option.value === CostTypes.COST || !filters.costType?.find((option) => option.value === CostTypes.DISCOUNT),
  );
};

const MainDashboardContext = createContext();
export const MainDashboardProvider = ({ children }) => {
  const { usersStore, appStore } = useRootStore();
  const [panels, setPanels] = useState(initialPanelsState);
  const costOptions = getCostOptions(usersStore)?.filter((option) => option.value !== AMORTIZE_COST_TYPES.SAVINGS);
  const excludeOptions = getExcludeOptions(usersStore);
  const [forecastEmptyStateText, setForecastEmptyStateText] = useState(null);
  const [forecastEmptyStateFiltersText, setForecastEmptyStateFiltersText] = useState(null);
  const defaultFilters = {
    costType: [
      { value: 'cost', label: 'Cost' },
      { value: 'discount', label: 'Discount' },
    ],
    cost:
      costOptions?.find((o) => o.value === 'unblended') || !costOptions?.length
        ? [
            {
              value: 'unblended',
              label: 'Unblended',
            },
          ]
        : [costOptions?.[0]],
  };
  if (usersStore.currDispUserCloudAccountType === CLOUD_TYPE_IDS.AWS) {
    defaultFilters.exclude = [{ value: 'tax', label: 'Tax' }];
  }
  const [filters, setFiltersState] = useState(defaultFilters);
  const [isFiltersChanged, setIsFiltersChanged] = useState(false);
  const isKeyCloakManagement = appStore.isKeyCloakManagement;

  useEffect(() => {
    if (!isKeyCloakManagement) {
      if (
        !isCostSelectedCostTypeSupportForecast(filters) ||
        filters.exclude?.length > 0 ||
        (filters.cost?.length > 0 &&
          costOptions?.length > 1 &&
          filters.cost[0]?.value !== AMORTIZE_COST_TYPES.UNBLENDED)
      ) {
        setForecastEmptyStateFiltersText(UnSupportForecastFilter);
        return;
      }
    } else if (
      !isCostSelectedCostTypeSupportForecast(filters) ||
      filters.cost.find((option) =>
        [
          AMORTIZE_COST_TYPES.PUBLIC_COST,
          AMORTIZE_COST_TYPES.DISTRIBUTED,
          AMORTIZE_COST_TYPES.LIST_UNIT_PRICE,
        ].includes(option.value),
      )
    ) {
      setForecastEmptyStateFiltersText(UnSupportForecastFilter);
      return;
    }
    setForecastEmptyStateFiltersText(null);
  }, [filters, isKeyCloakManagement, setForecastEmptyStateFiltersText, costOptions?.length]);

  const updatePanelLoadingState = (panelType, isLoading) => {
    if (panels[panelType]?.isLoading === isLoading) {
      return;
    }
    setPanels((prevPanels) => ({
      ...prevPanels,
      [panelType]: {
        ...prevPanels[panelType],
        isLoading: isLoading,
      },
    }));
  };

  const getExcludeFilters = () => {
    let excludeFilters = {};
    if (!filters.exclude) {
      return excludeFilters;
    }
    const filterMap = {
      tax: { key: AwsCommonFields.CHARGE_TYPE, value: ['Tax'] },
      marketplace: {
        key:
          usersStore.currDispUserCloudAccountType === CLOUD_TYPE_IDS.AWS
            ? AwsCommonFields.FAMILY_TYPE
            : AzureCommonFields.SERVICE,
        value:
          usersStore.currDispUserCloudAccountType === CLOUD_TYPE_IDS.AWS ? marketPlaceFamilyTypes : marketPlaceService,
      },
      support: { key: AwsCommonFields.SERVICE, value: supportServices },
    };

    filters.exclude.forEach((option) => {
      if (filterMap[option.value]) {
        excludeFilters[filterMap[option.value].key] = filterMap[option.value].value;
      }
    });
    return excludeFilters;
  };
  const getDynamicFilters = () => {
    const costFilters = (filters.cost || []).reduce((acc, option) => {
      const key = `is${option?.value.charAt(0).toUpperCase() + option?.value.slice(1)}`;
      acc[key] = true;
      return acc;
    }, {});
    return {
      ...costFilters,
      costType: filters.costType.map((f) => f.value),
      exclude: { ...getExcludeFilters() },
    };
  };

  const setFilters = (newFilters) => {
    setFiltersState(newFilters);
    if (!isFiltersChanged) {
      setIsFiltersChanged(true);
    }
  };

  const getOverrideAccount = (accountKey) => {
    const accounts = usersStore?.getMainUser?.()?.accounts;
    return accounts?.find((account) => Number(account.accountKey) === Number(accountKey));
  };
  const getCurrentDay = (accountKey) => {
    const localOverrideAccount = getOverrideAccount(accountKey);
    return accountKey && localOverrideAccount ? localOverrideAccount.lastProcessTime : DateFilter.getDate();
  };

  const dateFromLastMonth = (accountKey) => {
    return moment(getCurrentDay(accountKey)).subtract(1, 'months');
  };
  const dateFromSixMonthsAgo = (accountKey) => {
    return moment(getCurrentDay(accountKey)).subtract(6, 'months');
  };

  return (
    <MainDashboardContext.Provider
      value={{
        panels,
        updatePanelLoadingState,
        dateFromLastMonth,
        dateFromSixMonthsAgo,
        getCurrentDay,
        filters,
        setFilters,
        getDynamicFilters,
        getOverrideAccount,
        isFiltersChanged,
        costOptions,
        excludeOptions,
        forecastEmptyStateText,
        setForecastEmptyStateText,
        forecastEmptyStateFiltersText,
        setForecastEmptyStateFiltersText,
      }}
    >
      {children}
    </MainDashboardContext.Provider>
  );
};

export const useMainDashboardContext = () => useContext(MainDashboardContext);
